import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Patient } from '../../../../types/Patient';
import { QuestionnaireAndAnswers, QuestionnaireType } from '../../../../types/Questionnaire';
import { sortBy } from 'lodash';
import clsx from 'clsx';
import FormButtonDialog from './components/FormButtonDialog';
import { PacuChartsContext } from './PacuChartsContext';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { AssignmentRounded, ErrorOutline, InfoOutlined } from '@material-ui/icons';
import FormDialog from './components/FormDialog';
import ChartingForm from './ChartingForm';
import { getFormType } from './ChartingForms';
import { getChartingData, getChartingName, resolveChartsForProcedure } from './utils';
import { ChartIndicators } from './ChartInfo';
import { chartingStore } from './modules/chartingStore';
import { PreOpChartsContext } from './PreOpChartsContext';

// Debug logging helper for charting issues
const logChartingDebug = (context: string, data: Record<string, any>) => {
  if (process.env.NODE_ENV === 'development' || localStorage.getItem('DEBUG_CHARTING') === 'true') {
    console.debug(`[Charting:${context}]`, data);
  }
};

// Empty state message component
const ChartingEmptyState = ({
  message,
  details,
  isError = false,
}: {
  message: string;
  details?: string;
  isError?: boolean;
}) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    flex={1}
    padding={4}
    style={{ minHeight: 200, opacity: 0.7 }}
  >
    {isError ? (
      <ErrorOutline style={{ fontSize: 48, marginBottom: 16, color: '#f44336' }} />
    ) : (
      <InfoOutlined style={{ fontSize: 48, marginBottom: 16, color: '#9e9e9e' }} />
    )}
    <Typography variant="h6" color={isError ? 'error' : 'textSecondary'} align="center">
      {message}
    </Typography>
    {details && (
      <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: 8 }}>
        {details}
      </Typography>
    )}
  </Box>
);

interface SetChartingPinTimeoutProps {
  value: number;
}

export const SetChartingPinTimeout = ({ value }: SetChartingPinTimeoutProps) => {
  useEffect(() => {
    chartingStore.setState({ pinTimeout: value });
  }, [value]);

  return null;
};

export const ChartingSections = ({
  indicators,
  patient,
  patientId,
  questionnaireType,
  hospitalId,
}: {
  indicators: any;
  patient: Patient | undefined;
  patientId: number;
  questionnaireType: QuestionnaireType;
  hospitalId: number;
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [form, setForm] = useState<QuestionnaireAndAnswers | null>(null);

  // Debug logging for troubleshooting empty charting issues
  useEffect(() => {
    logChartingDebug('ChartingSections', {
      patientId,
      questionnaireType,
      hasPatient: !!patient,
      hasProcedure: !!patient?.procedure,
      hospitalId,
    });

    if (!questionnaireType) {
      console.warn(
        '[Charting] questionnaireType is undefined - this will cause empty charting. Check useTabs.ts for missing chartType.'
      );
    }
    if (patient && !patient.procedure) {
      console.warn('[Charting] Patient has no procedure linked - charting tiles will be empty.', {
        patientId: patient.id,
      });
    }
  }, [patient, patientId, questionnaireType, hospitalId]);

  const charts: QuestionnaireAndAnswers[] | undefined = sortBy(
    getChartingData(questionnaireType, patient),
    'questionnaire.order'
  );

  // Log when charts are empty for debugging
  useEffect(() => {
    if (patient && questionnaireType && charts.length === 0) {
      console.warn('[Charting] No charts found for patient', {
        patientId: patient.id,
        questionnaireType,
        hasProcedure: !!patient.procedure,
      });
    }
  }, [patient, questionnaireType, charts.length]);

  const [defaultSlide, setDefaultSlide] = useState(0);

  const handleClickOpen = (form: QuestionnaireAndAnswers) => {
    setOpen(true);
    setForm(form);
  };

  const openChartingWithSlide = (chartingId: number, slide: number) => {
    const chart = (charts || []).find(e => e?.questionnaire?.id === chartingId);
    if (chart) {
      handleClickOpen(chart);
      setDefaultSlide(slide);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setForm(null);
    setDefaultSlide(0);
  };

  const [view, setView] = useState('sections');

  return (
    <PacuChartsContext.Provider
      value={resolveChartsForProcedure(
        patient?.procedure?.pacuCharts ?? [],
        patient?.procedure?.chartTemplateVersion ?? 1,
      )}
    >
      <PreOpChartsContext.Provider
        value={resolveChartsForProcedure(
          patient?.procedure?.preOpCharts ?? [],
          patient?.procedure?.chartTemplateVersion ?? 1,
        )}
      >
        <Box display="flex" alignItems="stretch" style={{ gap: 48 }} flex={1}>
          <Box>
            <ToggleButtonGroup orientation="vertical" value={view} exclusive>
              <ToggleButton value="module" aria-label="module" onClick={() => setView('sections')}>
                <ViewModuleIcon />
              </ToggleButton>
              <ToggleButton value="quilt" aria-label="quilt" onClick={() => setView('form')}>
                <AssignmentRounded />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {(charts || []).map(
            (f: QuestionnaireAndAnswers, i: number) =>
              !!patient && (
                // f.questionnaire.content
                <FormDialog
                  defaultSlide={defaultSlide}
                  key={f.id}
                  questionnaireType={questionnaireType}
                  patient={patient}
                  handleClickOpen={handleClickOpen}
                  handleClose={handleClose}
                  form={f}
                  index={i}
                  open={open && f.id === form?.id}
                />
              )
          )}

          {view === 'sections' ? (
            <Box display="flex" flex={1} flexDirection="column">
              {(questionnaireType === QuestionnaireType.PreOpChart ||
                questionnaireType === QuestionnaireType.AnesthesiologistPreOpChart) &&
                !!patient && <ChartIndicators combined={indicators} />}

              {/* Show appropriate message based on state */}
              {!questionnaireType ? (
                <ChartingEmptyState
                  message="Unable to load charting"
                  details="Chart type is not configured. Please contact support."
                  isError
                />
              ) : !patient ? (
                <ChartingEmptyState
                  message="Loading patient..."
                  details="Please wait while patient data is being loaded."
                />
              ) : !patient.procedure ? (
                <ChartingEmptyState
                  message="No procedure found"
                  details="This patient does not have a procedure linked. Charting requires an active procedure."
                />
              ) : charts.length === 0 ? (
                <ChartingEmptyState
                  message={`No ${getChartingName(questionnaireType)} charts configured`}
                  details="No chart questionnaires have been set up for this chart type."
                />
              ) : (
                <Box className={clsx(classes.grid, { [classes.increaseColumns]: charts.length >= 7 })}>
                  {charts.map((f: QuestionnaireAndAnswers, i: number) => (
                    <Box key={f.id} display="flex">
                      {!!patient && <FormButtonDialog handleClickOpen={handleClickOpen} form={f} />}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ) : patient ? (
            <ChartingForm
              hospitalId={hospitalId}
              patient={patient}
              formType={getFormType(questionnaireType)}
              openChartingWithSlide={openChartingWithSlide}
            />
          ) : null}
        </Box>
      </PreOpChartsContext.Provider>
    </PacuChartsContext.Provider>
  );
};

export const useStyles = makeStyles(theme => ({
  grid: {
    flex: 1,
    padding: theme.spacing(4),
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridAutoRows: '1fr',
    gap: theme.spacing(2),
    alignItems: 'stretch',
    justifyContent: 'center',
  },
  increaseColumns: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));
